import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { LeftPanel } from '../components/game/LeftPanel';
import { CenterPanel } from '../components/game/CenterPanel';
import { RightPanel } from '../components/game/RightPanel';
import { TutorialActionBar } from '../components/game/TutorialActionBar';
import { MobilePanelTabs } from '../components/game/MobilePanelTabs';
import { TabletPanelToggle } from '../components/game/TabletPanelToggle';
import { GameStatusBar } from '../components/game/GameStatusBar';
import { themeConfig, DEFAULT_THEME } from '../constants/themes';

/**
 * Simplified GamePage for Tutorial mode
 * - No API calls for game initialization (handled by TutorialPage)
 * - No interrogation status checks
 * - No turn/consultation limits
 * - All actions processed client-side using tutorialScript
 */
export const TutorialGamePage = ({
  onActionComplete,
  onActionSelect,
  onModalClose,
  currentStepModalInfo,
  onPanelExpand,
  tutorialScript = [],
  tutorialNarratives = {},
  currentStep = 1
}) => {
  const { selectedTheme } = useGameStore();

  // Responsive panel state
  const [mobileActivePanel, setMobileActivePanel] = useState('scene');
  const [tabletActivePanel, setTabletActivePanel] = useState('scene');

  // Safely get theme with fallback - handle null/undefined and ensure valid theme exists
  const themeKey = (selectedTheme && themeConfig[selectedTheme]) ? selectedTheme : DEFAULT_THEME;
  const theme = themeConfig[themeKey];

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bg} flex flex-col pb-24`}>
      {/* Status Bar - Top, all breakpoints (tutorial mode) */}
      <GameStatusBar isTutorial />

      {/* Desktop Layout: 3 columns (>= 1024px) */}
      <div className="hidden lg:block flex-1 overflow-y-auto pr-[20%]">
        {/* Left and Center panels - scrollable */}
        <div className="flex">
          <div className="w-[37.5%] border-r border-white/10">
            <LeftPanel />
          </div>
          <div className="w-[62.5%] border-r border-white/10">
            <CenterPanel />
          </div>
        </div>
      </div>
      {/* Right panel - fixed position on desktop */}
      <div className="hidden lg:block fixed top-[3.5rem] right-0 w-[20%] h-[calc(100vh-3.5rem-6rem)] overflow-y-auto">
        <RightPanel onPanelExpand={onPanelExpand} />
      </div>

      {/* Tablet Layout: 2 columns with toggle (768px - 1023px) */}
      <div className="hidden md:flex lg:hidden flex-1 overflow-hidden">
        {/* Left: Always show Character panel */}
        <div className="w-[45%] border-r border-white/10 overflow-y-auto">
          <LeftPanel />
        </div>
        {/* Right: Toggle between Scene and Clues */}
        <div className="w-[55%] flex flex-col overflow-hidden">
          <TabletPanelToggle
            activePanel={tabletActivePanel}
            setActivePanel={setTabletActivePanel}
          />
          <div className="flex-1 overflow-y-auto">
            {tabletActivePanel === 'scene' ? <CenterPanel /> : <RightPanel onPanelExpand={onPanelExpand} />}
          </div>
        </div>
      </div>

      {/* Mobile Layout: Tab-based navigation (< 768px) */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden">
        <MobilePanelTabs
          activePanel={mobileActivePanel}
          setActivePanel={setMobileActivePanel}
        />
        <div className="flex-1 overflow-y-auto pb-4">
          {mobileActivePanel === 'character' && <LeftPanel />}
          {mobileActivePanel === 'scene' && <CenterPanel />}
          {mobileActivePanel === 'clues' && <RightPanel onPanelExpand={onPanelExpand} />}
        </div>
      </div>

      {/* Tutorial Action Bar - Client-side processing */}
      <TutorialActionBar
        onActionComplete={onActionComplete}
        onActionSelect={onActionSelect}
        onModalClose={onModalClose}
        currentStepModalInfo={currentStepModalInfo}
        tutorialScript={tutorialScript}
        tutorialNarratives={tutorialNarratives}
        currentStep={currentStep}
      />
    </div>
  );
};
